import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
  computed,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  inject,
} from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { ImageLoaderService, ModalService } from '@boels/helpers';
import { BoelsBaseSite, Language } from '@boels/models';
import { CultureFacade, SiteContextFacade } from '@boels/shared-state';
import { filter, withLatestFrom } from 'rxjs';
import { LanguageSelectorModalComponent } from '../language-selector-modal/language-selector-modal.component';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'boels-toggle-language-selector',
  templateUrl: './toggle-language-selector.component.html',
  styleUrls: ['./toggle-language-selector.component.scss'],
  standalone: true,
  imports: [NgTemplateOutlet],
  providers: [ImageLoaderService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleLanguageSelectorComponent {
  private modalService = inject(ModalService);
  private imageLoaderService = inject(ImageLoaderService);
  private siteContextFacade = inject(SiteContextFacade);
  private cultureFacade = inject(CultureFacade);

  textOnly = input<boolean>(false);
  cssClassForText = input<string>('');
  inMobileMenu = input<boolean>(false);

  private readonly _isActive = signal<boolean>(false);
  readonly isActive = computed(() => this._isActive());

  @Output() languageSelected = new EventEmitter<void>();
  @ViewChild('languageButton') languageButton: ElementRef;

  private readonly flagsFolderUrl = '/assets/images/icons/flags/Flag_';

  private readonly _baseSite = signal<BoelsBaseSite | undefined>(undefined);
  readonly baseSite$ = computed(() => this._baseSite());

  private readonly _language = signal<Language | undefined>(undefined);
  readonly language$ = computed(() => this._language());

  private readonly _baseSiteNativeName = signal<string | undefined>(undefined);
  readonly baseSiteNativeName = computed(() => this._baseSiteNativeName());

  private readonly _imageUrl = signal<string>('');
  readonly imageUrl = computed(() => this._imageUrl());

  modalConfig: MatDialogConfig = {
    panelClass: 'modal-language-selector',
  };

  constructor() {
    this.siteContextFacade
      .getActiveBaseSiteEntity()
      .pipe(
        filter((baseSite) => Boolean(baseSite)),
        withLatestFrom(this.cultureFacade.getActiveLanguage()),
        takeUntilDestroyed(),
      )
      .subscribe(([baseSite, language]) => {
        this._imageUrl.set(
          this.getCountryFlag((baseSite.uid as string).replace('rental', '')),
        );

        this._baseSiteNativeName.set(
          this.getBaseSiteNativeName(baseSite.country?.name as string),
        );

        const activeLanguage = baseSite.stores?.[0].languages?.find(
          (baseSiteLang) =>
            baseSiteLang.isocode && language.startsWith(baseSiteLang.isocode),
        );

        this._language.set(activeLanguage);
        this._baseSite.set(baseSite);
      });
  }

  handleMouseDown = () => {
    if (this.inMobileMenu()) {
      this._isActive.set(!this._isActive());
    }
  };

  openLanguageSelector(): void {
    const dialogRef = this.modalService.open(
      LanguageSelectorModalComponent,
      this.modalConfig,
    );

    dialogRef.afterClosed().subscribe(() => {
      // Reset active state when modal closes
      this._isActive.set(false);
      // Restore focus to the language button after modal closes
      if (this.languageButton?.nativeElement) {
        this.languageButton.nativeElement.focus();
      }
    });
  }

  getCountryFlag(countryIsoCode: string): string {
    return this.imageLoaderService.getImagePath(
      this.flagsFolderUrl,
      countryIsoCode,
    );
  }

  getBaseSiteNativeName(baseSiteNativeName: string): string {
    if (baseSiteNativeName.includes('/')) {
      return baseSiteNativeName.substring(0, baseSiteNativeName.indexOf('/'));
    } else {
      return baseSiteNativeName;
    }
  }
}
